import React from 'react';
import {useIntl} from 'react-intl';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {TileRow} from './totals-header-box-tiles/Tile_styled';
import {Title, Data} from './ProjectScopingTotals_styled';
import {ValueCell} from '../../../../../components/initial-plan/ValueCell';
import {Tooltip} from '@forecast-it/design-system';
import {ActualHoursTooltip} from './ProjectScopingTotalsTooltips';

interface TotalsRowProps {
	totalsField: string;
	translationId: string;
	tooltipContent?: React.ReactNode;
}

const MoneyRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData, settings} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell
					noJustify
					type={ValueCell.VALUE_TYPE.PRICE}
					value={totalsData[totalsField] || 0}
					currencySymbol={settings.currencySymbol}
				/>
			</Data>
		</TileRow>
	);
};

const TimeRow: React.FC<TotalsRowProps> = ({totalsField, translationId, tooltipContent}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();

	const DataComponent = ({showPointerOnHover}: {showPointerOnHover?: boolean}) => {
		return (
			<Data small showPointerOnHover={showPointerOnHover}>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData[totalsField] || 0} />
			</Data>
		);
	};

	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			{tooltipContent ? (
				<Tooltip content={tooltipContent} interactable={false} width="xl">
					<DataComponent showPointerOnHover />
				</Tooltip>
			) : (
				<DataComponent />
			)}
		</TileRow>
	);
};

const PercentageRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={totalsData[totalsField] || 0} />
			</Data>
		</TileRow>
	);
};

const PointsRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.POINTS} value={totalsData[totalsField] || 0} />
			</Data>
		</TileRow>
	);
};

const PlannedCost = {
	Cost: () => {
		const {projectData, settings} = useProjectTotals();

		return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
			<MoneyRow totalsField="plannedCost" translationId="common.cost" />
		) : projectData.useBaseline ? (
			<MoneyRow totalsField="baselineCost" translationId="common.cost" />
		) : (
			<MoneyRow totalsField="plannedCost" translationId="common.cost" />
		);
	},
	PlannedCost: () => {
		const {projectData, settings} = useProjectTotals();

		return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
			<MoneyRow totalsField="plannedCost" translationId="common.estimated_cost" />
		) : projectData.useBaseline ? (
			<MoneyRow totalsField="baselineCost" translationId="common.baseline_cost" />
		) : (
			<MoneyRow totalsField="plannedCost" translationId="common.estimated_cost" />
		);
	},
};

const ProgressRow: React.FC = () => <PercentageRow totalsField="progress" translationId="common.project_progress" />;

const ActualNonBillableHoursRow: React.FC = () => (
	<TimeRow totalsField="actualNonBillableHours" translationId="common.actual_non_billable_hours" />
);

const ProjectedTotalNonBillableHoursRow: React.FC = () => (
	<TimeRow totalsField="projectedNonBillableHours" translationId="project_scoping.projected_total_non_billable_hours" />
);

const ProjectedTotalHoursPercentageBillableRow: React.FC = () => {
	return (
		<PercentageRow
			totalsField="percentageProjectedTotalBillableHours"
			translationId="common.projected_total_hours_percentage_billable"
		/>
	);
};

const PlannedRevenueRow: React.FC = () => {
	const {projectData, settings} = useProjectTotals();

	return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<MoneyRow totalsField="plannedRevenue" translationId="common.revenue" />
	) : projectData.useBaseline ? (
		<MoneyRow totalsField="baselineRevenue" translationId="common.revenue" />
	) : (
		<MoneyRow totalsField="plannedRevenue" translationId="common.revenue" />
	);
};

const PlannedProfitRow: React.FC = () => {
	const {projectData, settings} = useProjectTotals();

	return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<MoneyRow totalsField="plannedProfit" translationId="common.profit" />
	) : projectData.useBaseline ? (
		<MoneyRow totalsField="baselineProfit" translationId="common.profit" />
	) : (
		<MoneyRow totalsField="plannedProfit" translationId="common.profit" />
	);
};

const ActualHoursPercentageBillableRow: React.FC = () => {
	return (
		<PercentageRow totalsField="percentageActualBillableHours" translationId="common.actual_hours_percentage_billable" />
	);
};

const EstimatedHoursRow: React.FC = () => {
	return <TimeRow totalsField="estimatedHours" translationId="common.estimated" />;
};

const PlannedHours = {
	Planned: () => {
		const {projectData, settings} = useProjectTotals();

		return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
			<TimeRow totalsField="allocatedHours" translationId="common.allocated" />
		) : projectData.useBaseline ? (
			<TimeRow totalsField="baselineHours" translationId="baseline.baseline" />
		) : (
			<TimeRow totalsField="estimatedHours" translationId="common.estimated" />
		);
	},
	PlannedHours: () => {
		const {projectData, settings} = useProjectTotals();

		return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
			<TimeRow totalsField="allocatedHours" translationId="project_budget.allocated_hours" />
		) : projectData.useBaseline ? (
			<TimeRow totalsField="baselineHours" translationId="baseline.hours" />
		) : (
			<TimeRow totalsField="estimatedHours" translationId="common.estimated_hours" />
		);
	},
};

const ActualHoursRow: React.FC<{translationId: string}> = ({translationId}) => {
	const {settings} = useProjectTotals();

	return settings.useAllocationsForActualValues ? (
		<TimeRow totalsField="actualHours" translationId={translationId} />
	) : (
		<TimeRow totalsField="actualHours" translationId={translationId} tooltipContent={<ActualHoursTooltip />} />
	);
};

const ActualHours = {
	Actual: () => <ActualHoursRow translationId="common.actual" />,
	ActualHours: () => <ActualHoursRow translationId="project_settings.financials.value_calculations.actual_hours" />,
};

const RemainingWorkHours = {
	RemainingWork: () => {
		return <TimeRow totalsField="remainingHours" translationId="common.remaining_work" />;
	},
	RemainingWorkHours: () => <TimeRow totalsField="remainingHours" translationId="common.remaining_work_hours" />,
};

const ProjectedTotalHours = {
	ProjectedTotal: () => {
		return <TimeRow totalsField="projectedTotalHours" translationId="common.projected_total" />;
	},
	ProjectedTotalHours: () => <TimeRow totalsField="projectedTotalHours" translationId="common.projected_total_hours" />,
};

const PlannedMarginRow: React.FC = () => {
	const {projectData, settings} = useProjectTotals();

	return settings.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<PercentageRow totalsField="plannedMargin" translationId="common.margin" />
	) : projectData.useBaseline ? (
		<PercentageRow totalsField="baselineMargin" translationId="common.margin" />
	) : (
		<PercentageRow totalsField="plannedMargin" translationId="common.margin" />
	);
};

const BaselineCost = {
	Baseline: () => <MoneyRow totalsField="baselineCost" translationId="baseline.baseline" />,
	Cost: () => <MoneyRow totalsField="baselineCost" translationId="common.cost" />,
};

const ActualCost = {
	ActualCost: () => <MoneyRow totalsField="actualCost" translationId="common.actual_cost" />,
	Actual: () => <MoneyRow totalsField="actualCost" translationId="common.actual" />,
};

const RemainingWorkCost = {
	RemainingWork: () => <MoneyRow totalsField="remainingCost" translationId="common.remaining_work" />,
};

const BaselineHours = {
	Baseline: () => <TimeRow totalsField="baselineHours" translationId="baseline.baseline" />,
	Hours: () => <TimeRow totalsField="baselineHours" translationId="common.hours" />,
};

const BaselineProfit = {
	Profit: () => <MoneyRow totalsField="baselineProfit" translationId="common.profit" />,
};

const BaselineMargin = {
	Margin: () => <PercentageRow totalsField="baselineMargin" translationId="common.margin" />,
};

const EstimatedPoints = {
	Estimated: () => <PointsRow totalsField="estimatedPoints" translationId="common.estimated" />,
};

const CompletedPoints = {
	Completed: () => <PointsRow totalsField="completedPoints" translationId="common.completed" />,
};

const RemainingPoints = {
	RemainingWork: () => <PointsRow totalsField="remainingPoints" translationId="common.remaining_work" />,
};

const ProjectedTotalRevenue = {
	Revenue: () => <MoneyRow totalsField="projectedTotalRevenue" translationId="common.revenue" />,
};

const ProjectedTotalCost = {
	ProjectedTotalCost: () => <MoneyRow totalsField="projectedTotalCost" translationId="common.projected_total_cost" />,
	ProjectedTotal: () => <MoneyRow totalsField="projectedTotalCost" translationId="common.projected_total" />,
	Cost: () => <MoneyRow totalsField="projectedTotalCost" translationId="common.cost" />,
};

const ProjectedTotalProfit = {
	Profit: () => <MoneyRow totalsField="projectedTotalProfit" translationId="common.profit" />,
};

const ProjectedTotalMargin = {
	Margin: () => <PercentageRow totalsField="projectedTotalMargin" translationId="common.margin" />,
};

const ActualValueOfService = {
	ActualValueOfService: () => <MoneyRow totalsField="actualValueOfService" translationId="common.actual_value_of_service" />,
	Actual: () => <MoneyRow totalsField="actualValueOfService" translationId="common.actual" />,
};

const ProjectedTotalValueOfService = {
	ProjectedTotalValueOfService: () => (
		<MoneyRow totalsField="projectedTotalValueOfService" translationId="common.projected_total_value_of_service" />
	),
	ProjectedTotal: () => <MoneyRow totalsField="projectedTotalValueOfService" translationId="common.projected_total" />,
};

const BaselineValueOfServiceRow: React.FC = () => (
	<MoneyRow totalsField="baselineValueOfService" translationId="baseline.baseline" />
);

const RemainingBudgetRow: React.FC = () => <MoneyRow totalsField="remainingBudget" translationId="common.remaining_budget" />;

const BillableValueOfServiceRow: React.FC = () => (
	<MoneyRow totalsField="billableActualValueOfService" translationId="common.billable_value_of_service" />
);

const ValueOfServiceRow: React.FC = () => (
	<MoneyRow totalsField="actualValueOfService" translationId="common.value_of_service" />
);

const OverUnderServicingRow: React.FC = () => (
	<MoneyRow totalsField="overUnderServicing" translationId="project_portfolio_report.over_under_servicing" />
);

const RemainingCostRow: React.FC = () => <MoneyRow totalsField="remainingCost" translationId="common.remaining_work_cost" />;

export const ProjectScopingTotalsRows = {
	RemainingCost: RemainingCostRow,
	PlannedCost: PlannedCost,
	ActualNonBillableHours: ActualNonBillableHoursRow,
	ProjectedTotalNonBillableHours: ProjectedTotalNonBillableHoursRow,
	ProjectedTotalHoursPercentageBillable: ProjectedTotalHoursPercentageBillableRow,
	PlannedRevenue: PlannedRevenueRow,
	PlannedProfit: PlannedProfitRow,
	PlannedMargin: PlannedMarginRow,
	PlannedHours: PlannedHours,
	ActualHours: ActualHours,
	RemainingWorkHours: RemainingWorkHours,
	ProjectedTotalHours: ProjectedTotalHours,
	BaselineCost: BaselineCost,
	ActualCost: ActualCost,
	RemainingWorkCost: RemainingWorkCost,
	BaselineHours: BaselineHours,
	BaselineProfit: BaselineProfit,
	BaselineMargin: BaselineMargin,
	EstimatedPoints: EstimatedPoints,
	CompletedPoints: CompletedPoints,
	RemainingPoints: RemainingPoints,
	ProjectedTotalRevenue: ProjectedTotalRevenue,
	ProjectedTotalCost: ProjectedTotalCost,
	ProjectedTotalProfit: ProjectedTotalProfit,
	ProjectedTotalMargin: ProjectedTotalMargin,
	ActualHoursPercentageBillable: ActualHoursPercentageBillableRow,
	ActualValueOfService: ActualValueOfService,
	ProjectedTotalValueOfService: ProjectedTotalValueOfService,
	BaselineValueOfService: BaselineValueOfServiceRow,
	BillableValueOfService: BillableValueOfServiceRow,
	RemainingBudget: RemainingBudgetRow,
	ValueOfService: ValueOfServiceRow,
	OverUnderServicing: OverUnderServicingRow,
	Progress: ProgressRow,
	EstimatedHours: EstimatedHoursRow,
};
